import { useTranslation } from "@sagot/i18n";
import { EmptyState } from "@sagot/ui";
import { usePermissions } from "../../../../session/permissions";
import { CardSkeleton, PanelError } from "../panels/panel-kit";
import { ArrivalMethodsCard } from "./arrival-methods-card";
import { FrameAllowlistCard } from "./frame-allowlist-card";
import { LaunchTierCard } from "./launch-tier-card";
import { LaunchUpsellCard } from "./launch-upsell-card";
import { OidcSlot } from "./oidc-card";
import { ScopedKeyCard } from "./scoped-key-card";
import { SessionLimitsCard } from "./session-limits-card";
import { TestLaunchCard } from "./test-launch-card";
import { useFrameOrigins } from "./use-frame-origins";
import { useLaunchSettings, type LaunchSettings } from "./use-launch-settings";
import { LaunchUrlsCard } from "./urls-card";

/** The "Show inside your pages" slot: its list loads on its own, and a failure is reported here, not inside the card. */
function FrameSlot() {
  const { t } = useTranslation();
  const probe = useFrameOrigins();
  if (probe.isPending) return <CardSkeleton rows={3} />;
  if (probe.isError) return <PanelError error={probe.error} onRetry={() => void probe.refetch()} title={t("launch.frame.loadError")} />;
  return <FrameAllowlistCard />;
}

/** Server keys are listed for people who manage API keys; everyone else sees where to ask. */
function KeySlot() {
  const { t } = useTranslation();
  if (usePermissions().can("api_keys.manage")) return <ScopedKeyCard />;
  return (
    <section className="card" aria-labelledby="key-denied-title" data-testid="scoped-key-denied">
      <div className="card-header">
        <div>
          <h2 id="key-denied-title" className="card-title">
            {t("launch.keys.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("launch.keys.subtitle")}</p>
        </div>
      </div>
      <EmptyState icon="lock" headingLevel={3} title={t("launch.keys.deniedTitle")} description={t("launch.keys.deniedBody")} />
    </section>
  );
}

function Cards({ settings }: { settings: LaunchSettings }) {
  return (
    <div className="max-w-4xl space-y-6" data-testid="secure-launch-cards">
      <LaunchTierCard settings={settings} />
      <ArrivalMethodsCard settings={settings} />
      <LaunchUrlsCard settings={settings} />
      <OidcSlot />
      <FrameSlot />
      <SessionLimitsCard settings={settings} />
      <KeySlot />
      <TestLaunchCard settings={settings} />
    </div>
  );
}

/**
 * Settings > Secure launch (`/w/:ws/settings/secure-launch`, M29): everything an integration engineer sets up once, in the
 * order of the mockup: how customers arrive (pin 1), the mint and return addresses (2), the sites that may frame the chat (3,
 * WP-M29-EDGE), session limits (4), the server key (5, WP-M29-KEYS) and the test launch (6).
 *
 * `GET /v1/launch-settings` decides what the page is: with the platform flag off it says so, without the plan feature it
 * explains Secure launch and offers the add-on (nothing editable), otherwise the six cards. No other Secure launch request is
 * made before that answer, so a workspace outside the rollout never logs a failed request.
 */
export function SecureLaunchPage() {
  const { t } = useTranslation();
  const settings = useLaunchSettings();
  const off = settings.isSuccess && !settings.data.enabled;
  const locked = settings.isSuccess && settings.data.enabled && !settings.data.entitled;
  return (
    <div className="space-y-6" data-testid="secure-launch-page">
      <header>
        <h2 className="text-xl font-extrabold tracking-tight">{t("launch.page.title")}</h2>
        <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t("launch.page.subtitle")}</p>
      </header>
      {settings.isPending ? <CardSkeleton rows={3} /> : null}
      {settings.isError ? <PanelError error={settings.error} onRetry={() => void settings.refetch()} title={t("launch.page.loadError")} /> : null}
      {off ? (
        <div className="card" data-testid="secure-launch-off">
          <EmptyState icon="shield-check" headingLevel={3} title={t("launch.page.unavailableTitle")} description={t("launch.page.unavailableBody")} />
        </div>
      ) : null}
      {locked ? (
        <div className="max-w-4xl">
          <LaunchUpsellCard />
        </div>
      ) : null}
      {settings.isSuccess && settings.data.enabled && settings.data.entitled ? <Cards settings={settings.data} /> : null}
    </div>
  );
}
